import * as React from 'react';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import { flatMap, uniq } from 'lodash';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import { Snackbar } from 'material-ui';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import waitForGraphQL from 'js/lib/waitForGraphQL';

import type {
  MultiGetRostersRequest,
  MultiGetRostersResponse,
} from '@coursera/grpc-types-enterpriseuser/coursera/proto/enterpriseuser/rosters/v1/rosters_api';

import WarningModal from 'bundles/internal-enterprise/components/DeleteMembershipWarningModal';
import JoinProgram from 'bundles/internal-enterprise/components/JoinProgram';
import OrganizationPrograms from 'bundles/internal-enterprise/components/OrganizationPrograms';
import constants from 'bundles/internal-enterprise/constants';
import programMembershipsManager from 'bundles/internal-enterprise/queries/ProgramMembershipsManager.graphql';
import type {
  ProgramMembershipsManagerQuery,
  ProgramMembershipsManagerQueryVariables,
} from 'bundles/internal-enterprise/queries/__generated__/ProgramMembershipsManager';
import type { ComposedOrganization, Contract } from 'bundles/internal-enterprise/types/Organization';
import type { ComposedProgramMembership } from 'bundles/internal-enterprise/types/ProgramMembership';
import { acceptProgramInvitation, deleteProgramMembership } from 'bundles/internal-enterprise/utils/apiUtils';
import Button from 'bundles/internal-ui/components/buttons/Button';
import Card from 'bundles/internal-ui/components/cards/Card';
import Naptime from 'bundles/naptimejs';
import EnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';
import type ProgramMembershipsV2 from 'bundles/naptimejs/resources/programMemberships.v2';
import ThirdPartyOrganizationV1 from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import Modal from 'bundles/phoenix/components/Modal';

import 'css!./__styles__/EnterpriseToolsHome';

const getContractIdForRosterId = (rosterId: $TSFixMe, userRosters: $TSFixMe) => {
  const userRoster = userRosters.find((roster: $TSFixMe) => roster.id === rosterId);
  return userRoster?.contractId;
};

export const composeOrganizations = (
  organizations: ThirdPartyOrganizationV1[],
  memberships: ComposedProgramMembership[],
  contracts: Contract[],
  userRosters: MultiGetRostersResponse['rosterRecords']
): ComposedOrganization[] => {
  return organizations
    .filter((org) => memberships.find((program) => program.organizationId === org.id))
    .map((org) =>
      Object.assign({}, org, {
        contracts: contracts
          .filter(
            (contract) =>
              contract.naptimeThirdPartyOrganization.id === org.id &&
              memberships.find((program) =>
                program.rosterIds
                  .map((rosterId: $TSFixMe) => getContractIdForRosterId(rosterId, userRosters))
                  .includes(contract.id)
              )
          )
          .map((contract: $TSFixMe) =>
            Object.assign({}, contract, {
              memberships: memberships
                .filter((program: $TSFixMe) =>
                  program.rosterIds
                    .map((rosterId: $TSFixMe) => getContractIdForRosterId(rosterId, userRosters))
                    .includes(contract.id)
                )
                .map((program: $TSFixMe) => Object.assign({}, program, { organizationSlug: org.slug })),
              thirdPartyOrganizationId: contract.naptimeThirdPartyOrganization.id,
              naptimeThirdPartyOrganization: undefined,
            })
          ),
      })
    );
};

type InputProps = {
  programMemberships: Array<ProgramMembershipsV2>;
  email: string;
  userId: string;
};

type Props = InputProps & {
  naptime: { refreshData: (x0: { resources: Array<string> }) => void };
  programs: Array<EnterpriseProgramsV1>;
  thirdPartyOrganizations: Array<ThirdPartyOrganizationV1>;
  contracts: Array<Contract>;
  userRosters: MultiGetRostersResponse['rosterRecords'];
};

type State = {
  errorMessage: string;
  showModal: boolean;
  showWarningModal: boolean;
  selectedData?: ComposedProgramMembership;
  showNotification: boolean;
};

class ProgramMembershipsManager extends React.Component<Props, State> {
  static contextTypes = {
    executeMutation: PropTypes.func,
  };

  declare context: LegacyContextType<typeof ProgramMembershipsManager.contextTypes>;

  state: State = {
    errorMessage: '',
    showModal: false,
    showWarningModal: false,
    selectedData: undefined,
    showNotification: false,
  };

  handleWarningModalClose = () => {
    this.setState({
      showWarningModal: false,
      selectedData: undefined,
    });
  };

  handleWarningModalAccept = () => {
    if (this.state.selectedData) {
      deleteProgramMembership(this.state.selectedData.id).then(
        () => {
          this.refreshData();
          this.handleWarningModalClose();
        },
        (error) => {
          this.setState({
            errorMessage: error.responseJSON.msg,
            showNotification: true,
          });
        }
      );
    }
  };

  getInvitationActions() {
    return [
      {
        icon: 'add',
        tooltip: 'Accept Invitation',
        onClick: (data: $TSFixMe) => this.acceptInvite(data),
      },
    ];
  }

  getMembershipActions() {
    return [
      {
        icon: 'clear',
        tooltip: 'Remove Membership',
        onClick: (data: $TSFixMe) => this.openWarningModal(data),
      },
    ];
  }

  processRows(
    programMemberships: Array<ProgramMembershipsV2>,
    programs: Array<EnterpriseProgramsV1>,
    contracts: Array<Contract>,
    userRosters: MultiGetRostersResponse['rosterRecords']
  ): Array<ComposedProgramMembership> {
    return programMemberships.reduce((acc, membership) => {
      const program: EnterpriseProgramsV1 | undefined = programs.find((p) => p.id === membership.programId);
      const programContract = contracts.find((contract) => {
        return (
          program &&
          membership.rosterIds
            .map((rosterId: $TSFixMe) => getContractIdForRosterId(rosterId, userRosters))
            .includes(contract.id)
        );
      });
      if (program && programContract) {
        acc.push(
          Object.assign(membership, {
            programName: program.name,
            programSlug: program.slug,
            typeName: 'enterpriseProgram',
            organizationId: program.thirdPartyOrganizationId,
            contractId: programContract && programContract.id,
          })
        );
      }
      return acc;
    }, [] as ComposedProgramMembership[]);
  }

  refreshData() {
    this.props.naptime.refreshData({
      resources: ['programMemberships.v2', 'enterprisePrograms.v1', 'thirdPartyOrganizations.v1'],
    });
  }

  acceptInvite(data: ComposedProgramMembership) {
    acceptProgramInvitation(data.invitationId, this.props.userId).then((r) => {
      r.json().then((responseData) => {
        if (r.ok) {
          this.refreshData();
          this.setState({
            errorMessage: '',
            showNotification: false,
          });
        } else {
          this.setState({
            errorMessage: JSON.parse(responseData.message).description ?? 'Something went wrong.',
            showNotification: true,
          });
        }
      });
    });
  }

  openWarningModal(data: ComposedProgramMembership) {
    this.setState({
      showWarningModal: true,
      selectedData: data,
    });
  }

  closeModal = () => {
    this.setState({ showModal: false });
  };

  showModal = () => {
    this.setState({ showModal: true });
  };

  render() {
    const { email, userId, programMemberships, programs, contracts, thirdPartyOrganizations, userRosters } = this.props;
    const { showModal, errorMessage, showWarningModal, showNotification } = this.state;

    const processedRows = this.processRows(programMemberships, programs, contracts, userRosters);
    const pendingInvitations = processedRows.filter((p) => p.membershipState === 'INVITED');
    const memberships = processedRows.filter((p) => p.membershipState === 'MEMBER');
    const deletedMemberships = processedRows.filter((p) => p.membershipState === 'DELETED_MEMBER');

    const orgsWithInvitations = composeOrganizations(
      thirdPartyOrganizations,
      pendingInvitations,
      contracts,
      userRosters
    );
    const orgsWithMemberships = composeOrganizations(thirdPartyOrganizations, memberships, contracts, userRosters);
    const orgsWithDeletedMemberships = composeOrganizations(
      thirdPartyOrganizations,
      deletedMemberships,
      contracts,
      userRosters
    );

    return (
      <Card>
        {errorMessage && <Snackbar open={showNotification} message={<span>{errorMessage}</span>} />}

        <h3>Pending Invitations ({pendingInvitations.length})</h3>
        <OrganizationPrograms
          organizations={orgsWithInvitations}
          columns={constants.columns.pendingInvitations}
          sortColumn="programName"
          tableActions={this.getInvitationActions()}
        />

        <h3>Current Memberships ({memberships.length})</h3>
        <OrganizationPrograms
          organizations={orgsWithMemberships}
          columns={constants.columns.programMemberships}
          sortColumn="createdAt"
          tableActions={this.getMembershipActions()}
        />

        <h3>Downgraded Memberships ({deletedMemberships.length})</h3>
        <OrganizationPrograms
          organizations={orgsWithDeletedMemberships}
          columns={constants.columns.programMemberships}
          sortColumn="createdAt"
          tableActions={this.getMembershipActions()}
        />

        <Button onClick={this.showModal} text="Add to another program" btnStyle="info" disabled={showModal} />

        {showModal && (
          <Modal modalName={`Add User ${userId} to Another Program`} handleClose={this.closeModal}>
            <h3>Add user {userId} to another program</h3>
            <JoinProgram
              programMembershipsWithPrograms={processedRows}
              email={email}
              userId={userId}
              refreshData={this.refreshData}
              closeModal={this.closeModal}
            />
          </Modal>
        )}
        {showWarningModal && (
          <WarningModal onClickYes={this.handleWarningModalAccept} onClosed={this.handleWarningModalClose} />
        )}
      </Card>
    );
  }
}

const rostersQuery = gql`
  query RostersQuery($input: MultiGetRostersRequest!) {
    response: byProgram(input: $input)
      @rest(
        type: "MultiGetRosters"
        path: "grpc/enterpriseuser/rosters/v1/RostersAPI/MultiGetRosters"
        method: "POST"
      ) {
      rosterRecords
    }
  }
`;

export default compose<Props, InputProps>(
  Naptime.createContainer(({ programMemberships }: { programMemberships: ProgramMembershipsV2[] }) => {
    const programIds: string[] = uniq(programMemberships.map((p) => p.programId));
    const rosterIds = uniq(flatMap(programMemberships, (programMembership) => programMembership.rosterIds));
    return {
      programs: EnterpriseProgramsV1.multiGet(programIds, {
        fields: ['id', 'thirdPartyOrganizationId', 'metadata'],
      }),
      rosterIds,
    };
  }),
  waitForGraphQL(rostersQuery, {
    skip: ({ rosterIds }: { rosterIds?: string[] }) => !rosterIds,
    options: ({ rosterIds }: { rosterIds: string[] }) => {
      const input: MultiGetRostersRequest = {
        rosterIds,
        includeDeleted: true,
      };
      return {
        variables: { input },
      };
    },
  }),
  Naptime.createContainer(
    ({ programs, data }: { programs: EnterpriseProgramsV1[]; data: { response: MultiGetRostersResponse } }) => {
      const orgIds = uniq(programs.map((program) => program.thirdPartyOrganizationId));
      const userRosters = data.response.rosterRecords;
      const contractIds = uniq(userRosters.map((userRoster) => userRoster.contractId));

      return {
        thirdPartyOrganizations:
          orgIds && orgIds.length > 0
            ? ThirdPartyOrganizationV1.multiGet(orgIds, {
                fields: ['id', 'name', 'slug'],
              })
            : [],
        contractIds,
        userRosters,
      };
    }
  ),
  graphql<
    { contractIds?: string[] },
    ProgramMembershipsManagerQuery,
    ProgramMembershipsManagerQueryVariables,
    { contracts?: Contract[] }
  >(programMembershipsManager, {
    skip: ({ contractIds }) => !contractIds || contractIds.length === 0,
    options: ({ contractIds }) => ({
      variables: { contractIds: contractIds ?? [] },
      context: { clientName: 'gatewayGql' },
    }),
    props: ({ data }) => {
      return {
        contracts: data?.EnterpriseContract?.queryContractByIds ?? [],
      };
    },
  })
)(ProgramMembershipsManager);
